import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, Button, DialogActions, DialogContent, DialogTitle, useMediaQuery, Divider, Typography, Autocomplete, TextField,
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { v4 as uuidv4 } from 'uuid';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InputText from '@/components/UI/InputText/InputText';
import HeaderWithTooltip from '@/components/UI/TableListEditable/HeaderWithTooltip/HeaderWithTooltip';
import { ALIGN, TABLE_ROW_SELECTION } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import TypeVersionsChipsInput from '@/components/UI/TypeVersionsChipsInput/TypeVersionsChipsInput';
import { CR_TYPE } from '@/helpers/constants';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import administrationServices from '@/services/administrationServices';
import {
  ModelSerieAdmin, ViaModelSerieStatePopup, ModelSeriesBackSave, ModelSeriesViewRow, Plant, PlantsViewRow,
  ViaPlantsStatePopup, ModelSerieAdminPopupSearch,
  PlantModelSeriesPopup, ViaModelSerieTVPlantRow, ColumnConfig, MultiSelectionColumnConfig,
} from '@/types';
import { MS_VIEW, PLANTS_VIEW } from './ModelSeries';
import {
  defaultMSStatePopup,
} from './modelSeries-constants';
import styles from './ModelSeries.module.scss';

const POPUP_MS_COLUMNS_NAMES = {
  MODEL_SERIES: 'modelSeries',
  TYPE_VERSION: 'typeVersion',
  PLANTS: 'plants',
  TYPE: 'type',
};

const POPUP_PLANTS_COLUMNS_NAMES = {
  MODEL_SERIES: 'modelSeries',
  TYPE_VERSION_RAW: 'typeVersionRaw',
};

const adaptModelSeries = (currentSelection: ModelSeriesViewRow[]): ViaModelSerieStatePopup[] => currentSelection
  .reduce((acc: ViaModelSerieStatePopup[], curr) => acc
    .concat({
      modelSeries: curr.modelSeries,
      typeVersions: curr?.children?.map(({ typeVersion }) => typeVersion) ?? [],
      msTVPlants: [],
      msType: curr.type || '',
    }), []);

const checkAllModelSeries = (mss: ViaModelSerieStatePopup[]) => mss.every((ms) => ms.modelSeries && ms.typeVersions.length && ms.msType.length > 0);

const getPlantsByMSTV = (msRows: ModelSeriesViewRow[], ms: string, tv: string) => {
  const msRow = msRows.find((mainRow) => mainRow.modelSeries === ms);
  const typeRow = msRow?.children?.find((typeRoww) => typeRoww.typeVersion === tv);

  return typeRow ? typeRow.rawPlants : [];
};

const getAllPlantsData = (selected: string[], allPlants: Plant[]) => allPlants.filter((plant) => selected
  ?.includes(plant.wkzNumber));

const getPlantsTablesData = (msTVPlants: PlantsViewRow[]): ViaPlantsStatePopup[] => msTVPlants.map((plant) => ({
  id: uuidv4(),
  plantName: plant.plant,
  plantNameWithWkz: `${plant.plant} (W${plant.wkzNumber})`,
  wkzNumber: plant.wkzNumber,
  table: plant.rawModelSeries.reduce((acc: PlantModelSeriesPopup[], curr) => acc.concat({
    id: uuidv4(),
    modelSeries: curr.modelSeries,
    typeVersion: curr.typeVersions.map((tv) => tv.code).join(', '),
    typeVersionRaw: curr.typeVersions.map((tv) => tv.code),
    typeVersionRawComplete: curr.typeVersions,
  }), []),
}));

const findTypeVersionInAllMS = (allMS: ModelSerieAdmin[], ms: string, tv: string) => {
  const msFound = allMS.find((msRow) => msRow.modelSeries === ms);
  const tvFound = msFound?.typeVersions?.find((tvRow) => tvRow.code === tv);

  return tvFound;
};

interface CreateEditModelSeriesPopupProps {
  editionMode: boolean;
  modelSeriesSelected?: ModelSeriesViewRow[];
  plantsSelected?: PlantsViewRow[];
  plants: Plant[];
  confirmChanges: (refresh: boolean) => void;
  tableView: string;
  allModelSeries: ModelSerieAdmin[];
  currentTableData: PlantsViewRow[];
}

const CreateEditModelSeriesPopup: React.FC<CreateEditModelSeriesPopupProps> = (props) => {
  const { editionMode, modelSeriesSelected = [], plantsSelected = [], plants, confirmChanges, tableView, allModelSeries, currentTableData } = props;
  const { t } = useTranslation();
  const theme: any = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [step, setStep] = useState(1);
  const [modelSeries, setModelSeries] = useState(defaultMSStatePopup);
  const [plantsTables, setPlantsTables] = useState<ViaPlantsStatePopup[]>([]);
  const [tablesSelection, setTablesSelection] = useState<{ [key: string]: string[] }>({});
  const [plantsTableAdd, setPlantsTableAdd] = useState<ViaPlantsStatePopup | null>(null);
  const [modelSeriesAdd, setModelSeriesAdd] = useState<ModelSerieAdminPopupSearch | null>(null);
  const [modelSeriesTVModified, setModelSeriesTVModified] = useState<ModelSeriesBackSave[]>([]);
  const { addToast } = useToastContext();
  const { setLoading } = useGlobalSpinnerContext();

  const crTypesOptions = [CR_TYPE.CAR, CR_TYPE.VAN];

  const matchMs = useCallback((model: string) => (currentTableData
    .filter((ms) => ms.rawModelSeries.some((item) => model.includes(item.modelSeries)))), [currentTableData]);

  const buildPlantsMultiSelection = useCallback((
    currentMs: ViaModelSerieStatePopup,
  ): MultiSelectionColumnConfig => ({
    options: plants,
    getAllSelectedData: (selected: unknown[]) => getAllPlantsData(selected as string[], plants),
    multiSelectionProps: { maxChip: 100, isPlants: true },
    onChange: ({ rowData, value }) => {
      const row = rowData as unknown as ViaModelSerieTVPlantRow;
      const newPlants = value as string[];

      setModelSeries((prevModelSeries) =>
        prevModelSeries.map((modelS) => {
          if (modelS.modelSeries !== currentMs.modelSeries) return modelS;

          return {
            ...modelS,
            msTVPlants: modelS.msTVPlants.map((tvPlants) =>
              tvPlants.typeVersion === row.typeVersion
                ? { ...tvPlants, plants: newPlants }
                : tvPlants,
            ),
          };
        }),
      );
    },
  }), [plants]);

  const handleTypeVersionsChange = useCallback((
    wkzNumber: string,
    row: PlantModelSeriesPopup,
    newValue: string[],
  ) => {
    const mss = row.modelSeries;

    const matched = matchMs(mss);
    const getMatchedPlants = matched.filter((foundMs) => foundMs.wkzNumber === wkzNumber);
    const getTypeVersions = getMatchedPlants[0]?.rawModelSeries
      ?.filter((foundMs) => foundMs.modelSeries === mss)[0]?.typeVersions ?? [];

    let typeVersion: { code: string; plants: string[] }[];

    if (newValue.length < getTypeVersions.length) {
    // Deleting
      const missingTypes = getTypeVersions.filter((tv) => !newValue.includes(tv.code));
      typeVersion = getTypeVersions.map((type) => ({
        code: type.code,
        plants: type.code === missingTypes[0]?.code
          ? missingTypes[0].plants.filter((plant) => plant !== wkzNumber)
          : type.plants,
      }));
    } else {
    // Adding or unchanged
      typeVersion = newValue
        .filter((type) => type)
        .map((type) => ({
          code: type,
          plants: getTypeVersions.find((tv) => tv.code === type)?.plants || [wkzNumber],
        }));
    }

    const mSerie: ModelSeriesBackSave = {
      modelSeries: mss,
      typeVersions: typeVersion,
    };
    setModelSeriesTVModified((prev) => [...prev, mSerie]);

    setPlantsTables((prevPlantsTables) =>
      prevPlantsTables.map((plant) => {
        if (plant.wkzNumber === wkzNumber) {
          return {
            ...plant,
            table: plant.table.map((tableRow) => (tableRow.modelSeries === mss
              ? {
                ...tableRow,
                typeVersion: newValue.join(', '),
                typeVersionRaw: newValue,
                typeVersionRawComplete: newValue.map((tv: string) => ({ code: tv })),
              }
              : tableRow)),
          };
        }

        return plant;
      }),
    );
  }, [matchMs]);

  const buildTypeVersionsCellRenderer = useCallback((
    wkzNumber: string,
  ) => {
    return function Renderer(params: { value: unknown; data: Record<string, unknown> }) {
      if (!params.data) return null;
      const currentValue = Array.isArray(params.value) ? params.value as string[] : [];
      const row = params.data as unknown as PlantModelSeriesPopup;

      return (
        <TypeVersionsChipsInput
          id={`typeversions-${row.id}`}
          value={currentValue}
          handleChange={(newVal) => handleTypeVersionsChange(wkzNumber, row, newVal)}
          maxLength={5}
          isTable
          variant="standard"
        />
      );
    };
  }, [handleTypeVersionsChange]);

  const getColumnsModelSeriesNew = useCallback((modelSeries: ViaModelSerieStatePopup): ColumnConfig[] => [
    { name: POPUP_MS_COLUMNS_NAMES.MODEL_SERIES, title: t('software.modelSeries'), flex: 1.3 },
    { name: POPUP_MS_COLUMNS_NAMES.TYPE_VERSION, title: t('administration.typeVersion'), flex: 1.3 },
    {
      name: POPUP_MS_COLUMNS_NAMES.PLANTS,
      title: t('administration.plants'),
      flex: 2.9,
      wrapText: true,
      multiSelection: buildPlantsMultiSelection(modelSeries),
    },
    { name: POPUP_MS_COLUMNS_NAMES.TYPE, title: t('administration.type') },
  ], [t, buildPlantsMultiSelection]);

  const getColumnsPlantsNew = useCallback((wkzNumber: string): ColumnConfig[] => [
    { name: POPUP_PLANTS_COLUMNS_NAMES.MODEL_SERIES, title: t('software.modelSeries'), flex: 1.25, align: ALIGN.CENTER },
    {
      name: POPUP_PLANTS_COLUMNS_NAMES.TYPE_VERSION_RAW,
      title: t('administration.typeVersion'),
      flex: 3.5,
      wrapText: true,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltipText: t('administration.msSection.typeVersionInputHelp') },
      cellRenderer: buildTypeVersionsCellRenderer(wkzNumber),

    },
  ], [t, buildTypeVersionsCellRenderer]);

  const saveModelSeriesData = () => {
    setLoading(true);
    let modelSeriesToSave: ModelSeriesBackSave[] = modelSeriesTVModified;

    if (tableView === MS_VIEW) {
      modelSeriesToSave = modelSeries.map((ms) => ({
        modelSeries: ms.modelSeries,
        typeVersions: ms.msTVPlants.map((tvP) => ({ code: tvP.typeVersion, plants: tvP.plants })),
        type: ms.msType,
      }));
    }

    if (plantsTables.some((plant) => plant.table.some((row) => row.typeVersionRaw.length === 0))) {
      addToast(t('administration.msSection.plantsTableNoTVError'), 'ERROR');
      setLoading(false);

      return;
    }

    const saveRequest = editionMode
      ? administrationServices.editModelSeries(modelSeriesToSave)
      : administrationServices.createModelSeries(modelSeriesToSave[0]);

    saveRequest
      .then(() => confirmChanges(true))
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setLoading(false));
  };

  const onNextClicked = async () => {
    if (step === 1 && tableView === MS_VIEW) {
      const msPlants: ViaModelSerieStatePopup[] = modelSeries.map((ms) => ({
        ...ms,
        msTVPlants: ms.typeVersions
          .map((tv) => ({
            modelSeries: ms.modelSeries,
            typeVersion: tv,
            type: ms.msType,
            plants: editionMode
              ? getPlantsByMSTV(modelSeriesSelected, ms.modelSeries, tv)
              : ms.msTVPlants
                .find((plnt) => plnt.typeVersion === tv)?.plants ?? [],
          })),
      }));
      setModelSeries(msPlants);
      setStep(2);
    } else {
      saveModelSeriesData();
    }
  };

  const validateStepStatus = !((step === 1 && checkAllModelSeries(modelSeries)) || step === 2);

  const setModelSerieData = (changedMS: ViaModelSerieStatePopup, changedIndex: number) => {
    setModelSeries(modelSeries.map((ms, index) => (index === changedIndex ? changedMS : ms)));
  };

  const onRemoveRow = () => {
    let modelSeriesTVModifiedNew: ModelSeriesBackSave[] = [...modelSeriesTVModified];
    const newPlantsTables = plantsTables.map((plant, index) => ({
      ...plant,
      table: plant.table.filter((row) => {
        const isNotInTheList = !tablesSelection?.[index]?.includes(row.id);

        if (!isNotInTheList) {
          const ms = modelSeriesTVModifiedNew.find((m) => m.modelSeries === row.modelSeries);

          if (ms) {
            const newMS = {
              modelSeries: ms.modelSeries,
              typeVersions: ms.typeVersions.map((tv) => ({
                code: tv.code,
                plants: tv.plants.filter((plantt) => plantt !== plant.wkzNumber),
              })),
            };
            modelSeriesTVModifiedNew = modelSeriesTVModifiedNew.map((ms) => (ms.modelSeries === modelSeriesAdd?.modelSeries ? newMS : ms));
          } else {
            const newMS = {
              modelSeries: row.modelSeries,
              typeVersions: row.typeVersionRaw.map((tv) => ({
                code: tv,
                plants: findTypeVersionInAllMS(allModelSeries, row.modelSeries, tv)?.plants?.filter((plantt) => plantt !== plant.wkzNumber) ?? [],
              })),
            };
            modelSeriesTVModifiedNew.push(newMS);
          }
        }

        return isNotInTheList;
      }),
    }));

    setModelSeriesTVModified(modelSeriesTVModifiedNew);
    setPlantsTables(newPlantsTables);
    setTablesSelection({});
  };

  const onAddNewModelSeries = () => {
    const matched = matchMs(modelSeriesAdd?.modelSeries ?? '');
    const newPlantsTables = plantsTables.map((plant) => (
      plant.wkzNumber === plantsTableAdd?.wkzNumber
        ? {
          ...plant,
          table: plant.table.concat([{
            id: uuidv4(),
            modelSeries: modelSeriesAdd?.modelSeries ?? '',
            typeVersion: modelSeriesAdd?.typeVersions.map((tv) => tv.code).join(', ') ?? '',
            typeVersionRaw: modelSeriesAdd?.typeVersions.map((tv) => tv.code) ?? [],
            typeVersionRawComplete: modelSeriesAdd?.typeVersions.map((tv) => ({ code: tv.code })) ?? [],
          }]),
        }
        : plant));

    const ms = modelSeriesTVModified.find((m) => m.modelSeries === modelSeriesAdd?.modelSeries);

    if (ms) {
      const newMS = {
        modelSeries: ms.modelSeries,
        typeVersions: ms.typeVersions.map((tv) => ({
          code: tv.code,
          plants: tv.plants.concat(plantsTableAdd?.wkzNumber ?? []).filter((value, index, self) => self.indexOf(value) === index),
        })),
      };
      setModelSeriesTVModified(modelSeriesTVModified.map((ms) => (ms.modelSeries === modelSeriesAdd?.modelSeries ? newMS : ms)));
    } else {
      const plantsArray = matched.map((ms) => ms.wkzNumber);

      const typeVersion = modelSeriesAdd?.typeVersions?.map((type) => ({
        code: type.code,
        plants: [...plantsArray, plantsTableAdd?.wkzNumber ?? ''],
      })) ?? [];

      const newMS: ModelSeriesBackSave = {
        modelSeries: modelSeriesAdd?.modelSeries ?? '',
        typeVersions: typeVersion,
      };

      setModelSeriesTVModified([...modelSeriesTVModified, newMS]);
    }
    setPlantsTables(newPlantsTables);
    setTablesSelection({});
    setModelSeriesAdd(null);
    setPlantsTableAdd(null);
  };

  useEffect(() => {
    if (tableView === MS_VIEW) {
      if (editionMode) {
        setModelSeries(adaptModelSeries(modelSeriesSelected));
      } else {
        setModelSeries(defaultMSStatePopup);
      }
    } else {
      setPlantsTables(getPlantsTablesData(plantsSelected));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Dialog id="create-edit-model-series" fullWidth open fullScreen={fullScreen} disableEnforceFocus>
        <DialogTitle className={styles.TitleContainer}>
          {t(`administration.msSection.${editionMode ? 'editMS' : 'addMS'}`)}
          {tableView === PLANTS_VIEW && (
            <Button
              disabled={!Object.values(tablesSelection).some((table: any) => table.length > 0)}
              onClick={onRemoveRow}
            >
              {t('removeRow')}
            </Button>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <div className={styles.DialogContainer}>
            {(step === 1 && tableView === MS_VIEW) && modelSeries.map((ms, index) => (
              <div className={styles.MSandTVContainer} key={`-${index}`}>
                <InputText
                  id="input-model-series"
                  value={ms.modelSeries}
                  isMandatory
                  maxLength={4}
                  label={t('hardware.modelSeries')}
                  handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => setModelSerieData({
                    ...ms,
                    modelSeries: ev.target.value.toUpperCase(),
                  }, index)}
                  formControlClass={styles.MoldelSeriesInput}
                  disabled={editionMode}
                />
                <TypeVersionsChipsInput
                  id="input-type-versions"
                  value={ms.typeVersions}
                  handleChange={(newTV) => setModelSerieData({ ...ms, typeVersions: newTV }, index)}
                  label={t('administration.typeVersion')}
                  maxLength={5}
                  fullWidth
                  isMandatory
                />
                <Dropdown
                  id="ms-type"
                  options={crTypesOptions}
                  value={ms.msType}
                  handleChange={(newTV) => setModelSerieData({
                    ...ms,
                    msType: typeof newTV === 'string' ? newTV : newTV.target.value,
                  }, index)}
                  label={t('administration.type', { number: 1 })}
                  withoutTranslations
                  isMandatory
                />

                {(editionMode && modelSeries.length > 1 && index !== modelSeries.length - 1) && (
                  <Divider className={styles.ModelSeriesSeparator} />
                )}
              </div>
            ))}
            {(step === 2 && tableView === MS_VIEW) && modelSeries.map((ms, index) => (
              <div key={ms.modelSeries}>
                <p className={styles.AssignVehicleText}>{t('administration.msSection.assignVehicle')}</p>
                <div className="w-100">
                  <TableListEditableNew
                    list={ms.msTVPlants as (ViaModelSerieTVPlantRow & Record<string, unknown>)[]}
                    columns={getColumnsModelSeriesNew(ms)}
                    isPaginated={false}
                  />
                </div>
                {(editionMode && modelSeries.length > 1 && index !== modelSeries.length - 1) && (
                  <Divider className={styles.PlantsSeparator} />
                )}
              </div>
            ))}

            {tableView === PLANTS_VIEW && (
              <div className={styles.PlantsTableContainer}>
                {plantsTables.map((plant, index) => (
                  <div className={styles.PlantTable} key={`${plant.plantName}-`}>
                    <div className={styles.HeaderTable}>
                      <Typography variant="h6" component="span">{plant.plantNameWithWkz}</Typography>
                      <Button onClick={() => setPlantsTableAdd(plant)}>{t('administration.fieldsMaintenance.addRow')}</Button>
                    </div>
                    <div className="w-100">
                      <TableListEditableNew
                        columnId="id"
                        hasCheckbox
                        list={plant.table as (PlantModelSeriesPopup & Record<string, unknown>)[]}
                        columns={getColumnsPlantsNew(plant.wkzNumber)}
                        selection={tablesSelection?.[index] as string[]}
                        onSelectionChange={(selection) => setTablesSelection({ ...tablesSelection, [index]: selection as string[] })}
                        isPaginated={false}
                        rowSelectionMode={TABLE_ROW_SELECTION.SINGLE}
                      />
                    </div>
                    {(plantsTables.length > 1 && index !== plantsTables.length - 1) && (
                      <Divider className={styles.PlantsSeparator} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <div className={styles.AddEditMSButtons}>
            {(step > 1 && tableView === MS_VIEW) && (
              <Button id="back-btn-modelseries" onClick={() => setStep(step - 1)} color="primary">
                {t('dialog.back')}
              </Button>
            )}
            <div className={styles.RightButtons}>
              {(tableView === MS_VIEW && validateStepStatus) && t('dialog.pleaseFillOut')}
              <Button
                id="save-next-btn-modelseries"
                variant="contained"
                disabled={tableView === MS_VIEW && validateStepStatus}
                onClick={onNextClicked}
                color="primary"
              >
                {t(`dialog.${(step === 1 && tableView === MS_VIEW) ? 'next' : 'save'}`)}
              </Button>
              <Button id="close-reject-btn-modelseries" onClick={() => confirmChanges(false)} color="primary">
                {t('dialog.cancel')}
              </Button>
            </div>
          </div>
        </DialogActions>
      </Dialog>

      {plantsTableAdd && (
        <ConfirmDialog
          confirmDisabled={!modelSeriesAdd}
          confirm={onAddNewModelSeries}
          close={() => {
            setPlantsTableAdd(null);
            setModelSeriesAdd(null);
          }}
          title={t('administration.msSection.addMSToPlant', { plant: plantsTableAdd?.plantNameWithWkz })}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.add')}
        >
          <Autocomplete
            id="input-add-model-series"
            value={modelSeriesAdd}
            options={allModelSeries.filter((ms) => !plantsTableAdd.table.find((p) => p.modelSeries === ms.modelSeries))}
            onChange={(ev, value) => setModelSeriesAdd(value)}
            getOptionLabel={(option) => option.modelSeries}
            renderInput={(params) => <TextField {...params} label={t('administration.msSection.addMS')} variant="outlined" />}
          />
          <br />
          <br />
          {modelSeriesAdd && (
            <TypeVersionsChipsInput
              id="input-add-type-version"
              handleChange={(newTVs: string[]) => setModelSeriesAdd({
                ...modelSeriesAdd,
                typeVersions: newTVs
                  .map((tv) => ({ code: tv })),
              })}
              value={modelSeriesAdd?.typeVersions.map((tv) => tv.code) ?? []}
              label={t('administration.typeVersion')}
              maxLength={5}
              fullWidth
            />
          )}
        </ConfirmDialog>
      )}

    </>
  );
};

export default CreateEditModelSeriesPopup;
